import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// Available themes
export const THEMES = {
  SIMPLE: 'simple',
  MODERN: 'modern'
};

// Both themes share one design: components color themselves with the ui-*
// tokens in src/index.css, whose values switch on <html data-theme>. Only the
// name and description differ here.
const SHARED_STYLES = {
  headerBg: 'bg-ui-surface',
  headerBorder: 'border-ui-divider',
  headerText: 'text-ui-ink-2',
  containerBg: 'bg-ui-page',
  contentBg: 'bg-ui-page',
  contentPadding: 'px-0 py-0',
  menuBg: 'bg-ui-surface',
  menuText: 'text-ui-ink-2',
  menuHover: 'hover:bg-ui-hover hover:text-ui-ink',
  menuActive: 'bg-ui-blue-bg text-ui-blue-fg',
  overlayBg: 'bg-ui-overlay/60',
};

export const THEME_CONFIG = {
  [THEMES.SIMPLE]: { name: 'Simple', description: 'Clean, light interface', ...SHARED_STYLES },
  [THEMES.MODERN]: { name: 'Modern', description: 'Premium dark interface', ...SHARED_STYLES },
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { user, setUser } = useAuth();

  // Initialize theme from user preferences or default to 'simple'
  const [theme, setThemeState] = useState(() => {
    return user?.ui_preferences?.app_theme || THEMES.SIMPLE;
  });

  // Sync theme when user data loads
  useEffect(() => {
    const savedTheme = user?.ui_preferences?.app_theme;
    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      setThemeState(savedTheme);
    }
  }, [user?.ui_preferences?.app_theme]);

  // The ui-* color tokens read this attribute
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // Save theme preference to database
  const setTheme = useCallback(async (newTheme) => {
    if (!Object.values(THEMES).includes(newTheme)) {
      console.error('Invalid theme:', newTheme);
      return;
    }

    setThemeState(newTheme);

    try {
      const res = await axios.put('/users/me/preferences', { app_theme: newTheme });
      if (user && setUser) {
        setUser({ ...user, ui_preferences: res.data });
      }
    } catch (err) {
      console.error('Failed to save theme preference:', err);
    }
  }, [user, setUser]);

  // Get current theme configuration
  const themeConfig = THEME_CONFIG[theme] || THEME_CONFIG[THEMES.SIMPLE];

  // Check if current theme is modern
  const isModern = theme === THEMES.MODERN;

  const value = {
    theme,
    setTheme,
    themeConfig,
    isModern,
    THEMES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
